import { Injectable, inject } from '@angular/core';
import { LunchLitelyClient } from '@peekops/lunch-litely';

import { AuthenticationService } from '../auth/authentication.service';

interface LunchLitelyWindow {
  CONFIG?: { lunchLitely?: { baseUrl?: string } };
  LL_API_KEY?: string;
}

export interface FlagContext {
  userId?: string;
  tenantId?: string;
  sessionId?: string;
  attributes?: Record<string, unknown>;
}

@Injectable({ providedIn: 'root' })
export class LunchLitelyService {
  private readonly authenticationService = inject(AuthenticationService);
  private readonly client: LunchLitelyClient | null = this.createClient();

  isEnabled(key: string, ctx?: FlagContext, defaultValue?: boolean): Promise<boolean> {
    return this.client
      ? Promise.resolve(this.client.isEnabled(key, { ...this.defaultContext(), ...ctx }, defaultValue))
      : Promise.resolve(defaultValue ?? false);
  }

  getVariation<T>(key: string, defaultValue: T, ctx?: FlagContext): Promise<T> {
    return this.client
      ? Promise.resolve(this.client.getVariation(key, defaultValue, { ...this.defaultContext(), ...ctx }))
      : Promise.resolve(defaultValue);
  }

  evaluate(key: string, ctx?: FlagContext): Promise<unknown> {
    return this.client
      ? Promise.resolve(this.client.evaluate(key, { ...this.defaultContext(), ...ctx }))
      : this.notConfigured();
  }

  evaluateBulk(keys: string[], ctx?: FlagContext): Promise<Record<string, unknown>> {
    return this.client
      ? Promise.resolve(this.client.evaluateBulk(keys, { ...this.defaultContext(), ...ctx }))
      : this.notConfigured();
  }

  private createClient(): LunchLitelyClient | null {
    const w = window as unknown as LunchLitelyWindow;
    const config = w.CONFIG?.lunchLitely;
    if (config?.baseUrl) {
      return new LunchLitelyClient({
        baseUrl: config.baseUrl,
        apiKey: w.LL_API_KEY ?? '',
      });
    }
    return null;
  }

  private defaultContext(): FlagContext {
    const user = this.authenticationService.getUserFromCookie();
    return {
      ...(user?.tenantId != null && { tenantId: String(user.tenantId) }),
      ...(user?.id != null && { userId: String(user.id) }),
    };
  }

  private notConfigured(): Promise<never> {
    return Promise.reject(new Error('LunchLitelyService is not configured'));
  }
}
